import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import Grid from '@mui/material/Grid2';
import styles from './FeatureCard.module.scss';

export interface FeatureCardProps {
  /** SvgIcon-compatible component (e.g. `@mui-symbols-material/w400/*`). */
  readonly icon: React.ComponentType<SvgIconProps>;
  /** The main title of the feature card */
  readonly title: string;
  /** The subtitle/description text */
  readonly subtitle?: string;
  /** Optional click handler for the card */
  readonly onClick?: () => void;
  /** Optional test id for testing */
  readonly testId?: string;
  /** Optional additional CSS class name */
  readonly className?: string;
  /** Optional aria-label for accessibility */
  readonly 'aria-label'?: string;
  /** Optional permission ID for authorization */
  readonly permissionId?: string;
  /** Whether this is a primary feature card (defaults to false) */
  readonly isPrimary?: boolean;
}

/**
 * FeatureCard component displays a feature with an icon, title, and subtitle.
 * Used for navigation or feature highlighting in the application.
 *
 * @param props - The component props
 * @returns The rendered feature card component
 */
const FeatureCardComponent: React.FC<Readonly<FeatureCardProps>> = ({
  icon: IconComponent,
  title,
  subtitle,
  onClick,
  testId,
  className,
  'aria-label': ariaLabel
}) => {
  const cardContent = (
    <CardContent className={styles.cardContent}>
      <Box className={styles.iconContainer}>
        <IconComponent fontSize="medium" aria-hidden="true" />
      </Box>
      <Typography variant="h6">
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" className={styles.subtitle}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  );

  const cardClasses = `${styles.featureCard} ${className ?? ''}`.trim();

  // Use provided aria-label or fallback to title/subtitle combination
  const accessibilityLabel = ariaLabel ?? (subtitle ? `${title} - ${subtitle}` : title);

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} sx={{ alignSelf: 'stretch' }}>
      {onClick ? (
        <button type="button" className={cardClasses} onClick={onClick} aria-label={accessibilityLabel} data-testid={testId}>
          {cardContent}
        </button>
      ) : (
        <Card className={cardClasses} data-testid={testId} aria-label={accessibilityLabel}>
          {cardContent}
        </Card>
      )}
    </Grid>
  );
};

export const FeatureCard = React.memo(FeatureCardComponent);
